import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import MeasurementOptionControls from "features/option-controls/measurements-option-controls";
import { useEffect, useMemo, useState } from "react";
import FormGroup from "shared/ui/FormGroup";
import Overlap from "shared/ui/icons/selfAssembly/Overlap";
import OverlapOneSide from "shared/ui/icons/selfAssembly/OverlapOneSide";
import Input from "shared/ui/input";
import Label from "shared/ui/label";
import { ToggleLayout } from "widgets/shared/toggle/components/ToggleLayout";
import ToggleButton from "../../../../widgets/shared/toggle/components/ToggleButton";
import Icon, { IconTypes } from "shared/ui/icon";
import RailsCeiling from "shared/ui/icons/selfAssembly/RailsCeiling";
import RailsWall from "shared/ui/icons/selfAssembly/RailsWall";
import { useSearchParams } from "react-router-dom";

interface Props {
  onShowInfoClick: ({
    title,
    description,
  }: {
    title: string;
    description: string;
  }) => void;
}
const SchuifdeurSelfInstallment = ({ onShowInfoClick }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    updateSelfAssembleData,
    updateSearchParam,
    selfAssembleDoorData,
    eigenwijs,
  } = useConfigurationOptionsContext();

  const [isDefaultDataInitialized, setIsDefaultDataInitialized] =
    useState(false);

  const [sparringHeight, setSparringHeight] = useState<string>("");

  const [heightError, setHeightError] = useState<string | null>(null);

  const doorMeasurements = useMemo(() => {
    if (eigenwijs)
      return {
        width: eigenwijs.sizeObjects[0].customWidth,
        height: eigenwijs.sizeObjects[0].customHeight,
      };
    return { width: 0, height: 0 };
  }, [eigenwijs?.sizeObjects]);

  useEffect(() => initializeDefaultTaatsdeurSelfInstallmentData(), []);
  useEffect(() => {
    if (!isDefaultDataInitialized) return;
    setDoorMeasurements(doorMeasurements);
  }, [isDefaultDataInitialized, doorMeasurements]);

  const initializeDefaultTaatsdeurSelfInstallmentData = () => {
    const data = { ...selfAssembleDoorData };
    data.measurements = {
      rotation: "left", //Default
      easyToReach: false, //Default
      deliverInParts: false, //Default
      onLocation: false, //Default
      extraFloor: true, //Default
      typePlafond: "beton", //Default
      view: "zelfmontage", //Default
      leftStelprofiel: false, //Default
      rightStelprofiel: false, //Default
      hasFloorHeating: false, //Default
      pipe: false, //Default
      plint: false, //Default
      // locationPlacement: "koof-plafond",
    };

    if (searchParams.get("label")) data.label = searchParams.get("label") || "";
    if (searchParams.get("locationPlacement"))
      data.measurements.locationPlacement =
        searchParams.get("locationPlacement") || "";
    if (parseInt(searchParams.get("sparingPlafond") || ""))
      data.measurements.sparingPlafond = parseInt(
        searchParams.get("sparingPlafond") || "",
      );
    setSparringHeight(searchParams.get("sparingPlafond") || "");
    if (searchParams.get("slideToWall"))
      data.measurements.slideToWall =
        searchParams.get("slideToWall") === "true";

    updateSelfAssembleData(data);
    setIsDefaultDataInitialized(true);
  };

  const setDoorMeasurements = (doorMeasurements: {
    width: number;
    height: number;
  }) => {
    const data = { ...selfAssembleDoorData };
    data.measurements.sponningsBreedte = doorMeasurements.width;
    data.measurements.sponningsHoogte = doorMeasurements.height;
    data.measurements.skews = {
      width: Array(4)
        .fill(0)
        .map(() => ({
          size: doorMeasurements.width,
          offset: 0,
        })),
      height: Array(3)
        .fill(0)
        .map(() => ({
          size: doorMeasurements.height,
          offset: 0,
        })),
      openHeight: [
        [],
        [],
        [
          {
            offset: 0, //Default
          },
          {
            offset: 0, //Default
          },
        ],
      ],
    };
    updateSelfAssembleData(data);
  };

  const setLabel = (label: string) => {
    updateSelfAssembleData({
      ...selfAssembleDoorData,
      label,
    });
    updateSearchParam("label", label);
  };

  const setHeightType = (type: string) => {
    const data = { ...selfAssembleDoorData };
    data.measurements.locationPlacement = type;

    if (type === "wand") {
      data.measurements.sparingPlafond = undefined;
      updateSearchParam("sparingPlafond", "");
    } else {
      data.measurements.sparingPlafond = undefined;
      searchParams.delete("sparingPlafond");
      setSearchParams(searchParams);
    }

    updateSearchParam("locationPlacement", type);
    updateSelfAssembleData(data);
  };

  const setOpeningHeight = (height: string) => {
    setSparringHeight(height);

    if (!height) {
      setHeightError("Dit veld is verplicht");
      // return;
    }
    if (Number(height) < 85) {
      setHeightError(
        "De ruimte van bovenzijde rails tot plafond moet minimaal 85mm zijn om de rails correct te kunnen monteren. Is deze afstand kleiner? Kies dan voor plafondmontage.",
      );
      // return;
    }

    if (height && Number(height) >= 85) {
      setHeightError(null);
    }

    if (Math.abs(Number(height)) > 0) {
      const data = { ...selfAssembleDoorData };
      data.measurements.sparingPlafond = Number(height);

      updateSearchParam("sparingPlafond", height);
      updateSelfAssembleData(data);
    }
  };

  const setWidthType = (type: string) => {
    const data = { ...selfAssembleDoorData };
    if (type === "2-sides") {
      data.measurements.slideToWall = false;
      updateSearchParam("slideToWall", "false");
    } else if (type === "1-1-sides") {
      data.measurements.slideToWall = true;
      updateSearchParam("slideToWall", "true");
    }

    updateSelfAssembleData(data);
  };

  // Determine if the height type is "koof-plafond"
  const isHeightTypePlafond =
    selfAssembleDoorData.measurements.locationPlacement === "koof-plafond";
  const isHeightTypeWand =
    selfAssembleDoorData.measurements.locationPlacement === "wand";

  // Determine if the width type is "2-sides"
  const isWidthType2Sides =
    selfAssembleDoorData.measurements.slideToWall === false;

  const isWidthType11Sides =
    selfAssembleDoorData.measurements.slideToWall === true;

  return (
    <div className="gg-flex gg-flex-col gg-gap-y-5">
      <FormGroup>
        <Label
          htmlFor="self-installment-label"
          className="gg-mb-2 gg-font-bold"
        >
          Label
        </Label>
        <Input
          placeholder={
            "Vul hier de ruimte in waar de deur komt, bijv. Woonkamer."
          }
          key={`self-installment-label`}
          className={
            "gg-border-dark-blue-200 gg-py-3 gg-pr-12 placeholder:gg-italic placeholder:gg-text-grey-200"
          }
          id={`self-installment-label`}
          data-testid={"self-installment-label"}
          type={"string"}
          value={selfAssembleDoorData.label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </FormGroup>

      <MeasurementOptionControls />

      <FormGroup>
        <Label
          htmlFor="width-type"
          className="gg-font-bold"
          onInfoButtonClick={() =>
            onShowInfoClick({
              title: "Schuifdeur positie",
              description:
                "Omdat de schuifdeur de deuropening goed moet kunnen afsluiten, willen we graag van je weten hoe veel de deur moet overlappen. Komt de schuifdeur midden op de muur? Dan heb je aan twee kanten overlap. Komt de schuifdeur aan het uiteinde van een muur? Dan heb je slechts aan één kant overlap.",
            })
          }
          showInfoButton
        >
          Schuifdeur positie
        </Label>

        <h3 className="gg-mb-2 gg-mt-1 gg-text-sm">Maak hieronder je keuze</h3>

        <ToggleLayout>
          <ToggleButton
            isActive={isWidthType2Sides}
            onClick={() => setWidthType("2-sides")}
            roundedClass="gg-rounded-l-sm gg-border-l gg-grid gg-place-items-center "
            layoutId="plafonToggle"
            dataTestId="plafonToggle2kant"
          >
            <Overlap
              className="gg-mb-2 gg-pt-2 sm:gg-mb-6"
              isActive={isWidthType2Sides}
            />
            Overlap
          </ToggleButton>

          <ToggleButton
            isActive={isWidthType11Sides}
            onClick={() => setWidthType("1-1-sides")}
            roundedClass="gg-rounded-r-sm gg-border-r gg-grid gg-place-items-center "
            layoutId="plafonToggle"
            dataTestId="plafonToggle1kant"
          >
            <OverlapOneSide
              className="gg-mb-2 gg-pt-2 sm:gg-mb-6"
              isActive={isWidthType11Sides}
            />
            Overlap 1 kant
          </ToggleButton>
        </ToggleLayout>
      </FormGroup>

      <FormGroup>
        <Label htmlFor="height-type" className="gg-font-bold">
          Montagewijze rails
        </Label>

        <h3 className="gg-mb-2 gg-mt-2 gg-text-sm">Maak hieronder je keuze</h3>

        <ToggleLayout>
          <ToggleButton
            isActive={isHeightTypePlafond}
            onClick={() => setHeightType("koof-plafond")}
            roundedClass="gg-rounded-l-sm gg-border-l"
            layoutId="heightToggle"
            dataTestId="heightTogglePlafond"
          >
            <RailsWall
              className="gg-mx-auto gg-mb-2 gg-pt-2 sm:gg-mb-6"
              isActive={isHeightTypePlafond}
            />
            Aan het plafond
          </ToggleButton>

          <ToggleButton
            isActive={isHeightTypeWand}
            onClick={() => setHeightType("wand")}
            roundedClass="gg-rounded-r-sm gg-border-r"
            layoutId="heightToggle"
            dataTestId="heightToggleWand"
          >
            <RailsCeiling
              className="gg-mx-auto gg-mb-2 gg-pt-2 sm:gg-mb-6"
              isActive={isHeightTypeWand}
            />
            Tegen de muur
          </ToggleButton>
        </ToggleLayout>
      </FormGroup>

      {selfAssembleDoorData.measurements.locationPlacement === "wand" && (
        <FormGroup>
          <Label htmlFor="opening-height" className="gg-font-bold">
            Hoogte sparing naar plafond
          </Label>
          <h3 className="gg-mb-2 gg-mt-2 gg-text-sm">
            Bekijk inmeetinstructies voor extra info
          </h3>
          <Input
            key={`opening-height`}
            className={"gg-border-dark-blue-200 gg-py-3 gg-pr-12"}
            id={`width-type`}
            data-testid={"opening-height"}
            type={"number"}
            placeholder="Hoogte sparing naar plafond"
            value={sparringHeight}
            onChange={(e) => setOpeningHeight(e.target.value)}
            suffix={"mm"}
            error={!!heightError}
            errorText={heightError ?? ""}
          />
        </FormGroup>
      )}
    </div>
  );
};
export default SchuifdeurSelfInstallment;
